import {groupByUniqueKey, mapObject} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {useCallback, useState} from 'react';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import DocumentActions from 'notebook/documents/DocumentActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import DocumentUtil from 'notebook/documents/DocumentUtil';
import {ReorderStepEvent} from 'procedures/design/method-config/MethodEditor';
import {MAX_STEP_NAME_LENGTH, PROCEDURE_PRODUCT_LIMITS} from 'procedures/design/ProcedureDesignConstants';
import {makePlaceholderNameClass} from 'procedures/design/procedureMethodNodeCommonConsts';
import {ADDING_STEP_TEXT, STEP_NAME_PLACEHOLDER} from 'procedures/design/step-config/StepConfigConstants';
import {StepGroupUpdater} from 'procedures/design/step-config/StepConfigEditor';
import {
  addStepDirectlyToStepGroup,
  collectAllStepNames,
  copyStepWithPersistenceInfo,
  findStep,
  getNextStepCidAfterRemove,
  getUpdatedStepGroupsWithCopiedStep,
  makeEmptyStepGroups,
} from 'procedures/design/step-config/stepManagementUtils';
import {DraftStateEditorInfo} from 'procedures/design/step-config/useStepNameDraftStateEditor';
import {
  Method,
  PROCEDURE_STEP_GROUP_TYPE_TO_LABEL,
  ProcedureFlowchart,
  ProcedureStepConfig,
  ProcedureStepGroup,
  ProcedureStepGroupType,
} from 'procedures/ProcedureDomainTypes';
import {
  BlockingSaveObjectSource,
  BlockingSaveOperation,
  ProcedurePageContext,
} from 'procedures/ProcedurePageContext';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import MethodSidepanelContext from 'procedures/processes/MethodSidepanelContext';
import ProcedureStepGroupWrapper from 'procedures/shared-components/step-config/step-nav/ProcedureStepGroup';
import ProcedureStepsListItem from 'procedures/shared-components/step-config/step-nav/ProcedureStepsListItem';
import AddStepModal, {StepPlacement} from 'procedures/worksheets/AddStepModal';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_POWDER_LIGHT, GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import TextInput from 'ui-library/inputs/TextInput';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import runModal from 'ui-library/runModal';
import {ElementMouseEvent, InputChangeEvent} from 'util/Types';
import {NON_BREAKING_SPACE} from 'util/UnicodeSymbols';

import useDragDropSidebar, {DragAndDropStepItemProperties, DragAndDropSystem} from './useDragDropSidebar';

interface StepSelectorSidebarProps {
  method: Method;
  onChangeSteps: (stepGroupUpdater: StepGroupUpdater) => void;
  selectedStepCid: string | null;
  onChangeSelection: (newStepId: string) => void;
  onReorderedStep: (params: ReorderStepEvent) => void;
  sidebarWidth: number;
  locked: boolean;
  draftStateEditorInfo: DraftStateEditorInfo;
  dragAndDropSystem?: DragAndDropSystem;
}

interface AddStepButtonProps {
  onClick: () => void;
  group: ProcedureStepGroupType;
  locked: boolean;
  isStepLimitReached: boolean;
  dragAndDropProperties: DragAndDropStepItemProperties;
}

function StepLimitTooltip(props: {
  children: JSX.Element;
  isStepLimitReached: boolean;
  // procedureType is passed in via props because this component is used in the overflow dropdown menu, which doesn't have access to ProcedurePageContext
  procedureType: ProcedureType;
}): JSX.Element {
  return (
    <Popover
      placement={FloatingPlacement.TOP}
      renderContent={() => (
        <div>
          {`Limit of ${PROCEDURE_PRODUCT_LIMITS.STEPS_PER_METHOD} steps per ${getProcedureUIString(
            ProceduresStringKeys.METHOD,
            props.procedureType,
            {
              stringCase: StringCase.LOWER,
            }
          )} reached`}
        </div>
      )}
      isDisabled={!props.isStepLimitReached}
    >
      {props.children}
    </Popover>
  );
}

function AddStepButton(props: AddStepButtonProps): JSX.Element {
  const {procedureType} = ProcedurePageContext.useContext();

  return (
    <StepLimitTooltip isStepLimitReached={props.isStepLimitReached} procedureType={procedureType}>
      <div
        className={css(
          styles.addStepButtonWrapper,
          props.dragAndDropProperties.isBeingDraggedOver && styles.elementBeingHoveredOver
        )}
        {...props.dragAndDropProperties.eventHandlers}
      >
        <Button
          buttonStyle={ButtonStyle.TEXT}
          onClick={props.onClick}
          size={ButtonSize.MEDIUM}
          icon="compose"
          text="Add step"
          dataTestAttrs={{
            component: 'AddStepButton',
            element: PROCEDURE_STEP_GROUP_TYPE_TO_LABEL[props.group],
          }}
          disabled={props.locked || props.isStepLimitReached}
        />
      </div>
    </StepLimitTooltip>
  );
}

/**
 * IMPORTANT NOTE: This component cannot access ReactContext from outside the menu, such as ProcedurePageContext
 */
function DuplicateStepOptionWithStepLimit(props: {
  isStepLimitReached: boolean;
  procedureType: ProcedureType;
}): JSX.Element {
  return (
    <StepLimitTooltip isStepLimitReached={props.isStepLimitReached} procedureType={props.procedureType}>
      <>Duplicate</>
    </StepLimitTooltip>
  );
}

function getInfoIconCopyForStepGroup(
  procedureType: ProcedureType,
  groupType: ProcedureStepGroupType
): string | null {
  const introCopy = `When this ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {stringCase: StringCase.LOWER})} is executed, we will generate `;
  switch (groupType) {
    case ProcedureStepGroupType.PRE_RUN:
      return `${introCopy}Pre-run steps for users to capture data against the materials, parameters, and equipment defined in Definitions.`;
    case ProcedureStepGroupType.RUN:
      return `${introCopy}a Run step to capture data about material outputs.`;
    default:
      return null;
  }
}

/**
 * We keep these functions outside the component to ensure they don't have access to any (potentially stale) closure values
 */
// Exported for testing
export function _moveStepArbitrarily(
  stepGroups: Array<ProcedureStepGroup>,
  stepCidToMove: string,
  targetStepCid: string
): null | {
  stepBeingMoved: ProcedureStepConfig;
  stepGroupToMoveFrom: ProcedureStepGroupType;
  newStepGroups: Array<ProcedureStepGroup>;
} {
  let stepGroupToMoveFrom: ProcedureStepGroupType;
  let newPosition: number;
  const originalStepToMove = findStep(stepGroups, stepCidToMove);

  const newStepGroups = produce(stepGroups, (draftStepGroups) => {
    // Ensure we have a valid operation
    const stepToMove = findStep(draftStepGroups, stepCidToMove);
    const isValidDropTarget =
      isCidForAddStepButton(targetStepCid) || null != findStep(draftStepGroups, targetStepCid);
    assert(stepToMove != null && isValidDropTarget, 'Invalid drag and drop operation', {
      stepCidToMove,
      targetStepCid,
      stepGroups,
    });

    // Find & remove the step from its original stepGroup
    for (const stepGroup of draftStepGroups) {
      const stepIndexToMoveFrom = stepGroup.steps.findIndex((s) => s.cid === stepCidToMove);
      if (stepIndexToMoveFrom !== -1) {
        stepGroup.steps.splice(stepIndexToMoveFrom, 1);
        stepGroupToMoveFrom = stepGroup.group;
      }
    }

    // Add it to the new stepGroup
    for (const stepGroup of draftStepGroups) {
      let stepIndexToMoveTo = stepGroup.steps.findIndex((s) => s.cid === targetStepCid);

      // The user dropped onto the "add step" button, which means: put at the end of the group
      if (stepIndexToMoveTo === -1 && targetStepCid === makeCidForAddStepButton(stepGroup.group)) {
        stepIndexToMoveTo = stepGroup.steps.length;
      }

      if (stepIndexToMoveTo !== -1) {
        stepGroup.steps.splice(stepIndexToMoveTo, 0, stepToMove);
        newPosition = stepIndexToMoveTo;
        break;
      }
    }
  });

  return newPosition! != null && originalStepToMove != null && stepGroupToMoveFrom! != null
    ? {stepBeingMoved: originalStepToMove, stepGroupToMoveFrom, newStepGroups}
    : null;
}

const ADD_STEP_BUTTON_CID_PREFIX = 'addStep_';

// Each "Add step" button needs to have a unique CID to identify it as a drop target when reordering steps
// (Exported for testing)
export function makeCidForAddStepButton(group: ProcedureStepGroupType): string {
  return `${ADD_STEP_BUTTON_CID_PREFIX}${group}`;
}

function isCidForAddStepButton(cid: string): boolean {
  return cid.startsWith(ADD_STEP_BUTTON_CID_PREFIX);
}

export default function StepSelectorSidebar(props: StepSelectorSidebarProps): JSX.Element {
  const {onChangeSteps, onChangeSelection, onReorderedStep, method, draftStateEditorInfo} = props;
  const {procedureType} = ProcedurePageContext.useContext();
  const stepGroups = method.stepGroups ?? makeEmptyStepGroups(procedureType);
  const isStepLimitReached =
    stepGroups
      .map((stepGroup) => stepGroup.steps.length)
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0) >=
    PROCEDURE_PRODUCT_LIMITS.STEPS_PER_METHOD;
  const [currentlyEditingStepCid, setCurrentlyEditingStepCid] = useState<string | null>(null);
  const {saveImmediately} = ProcedurePageContext.useContext();
  // TODO(BNCH-133735): Move this into a hook which will delegate to one of the two versions of saveImmediately
  const {saveImmediately: powSaveImmediately} = MethodSidepanelContext.useContext();

  const {draftName, setDraftName, onFocus, onBlur, storeDraftName, handleEnterAndEscape} =
    draftStateEditorInfo;

  const dragDropTools = useDragDropSidebar({
    system: props.locked ? DragAndDropSystem.NONE : DragAndDropSystem.FREE,
    droppableCidsByGroup: mapObject(
      groupByUniqueKey(stepGroups, ({group}) => group),
      (group) => group.steps.map(({cid}) => cid).concat([makeCidForAddStepButton(group.group)])
    ),
    allDraggableCids: stepGroups
      .map((group) => group.steps)
      .flat()
      .map((step) => step.cid),
    onDrop: (draggedItemCid: string, droppedOnCid: string) => {
      moveStepArbitrarily(draggedItemCid, droppedOnCid);
    },
  });

  /**
   Method to move a step in the given number of direction indexes
   */
  const moveStep = useCallback(
    (stepGroupIndex: number, stepIndex: number, direction: number) => {
      onChangeSteps((sg) =>
        produce(sg, (draftStepGroups) => {
          const {steps} = draftStepGroups[stepGroupIndex];
          const stepToMove = steps[stepIndex];
          steps.splice(stepIndex, 1); // Remove
          steps.splice(stepIndex + direction, 0, stepToMove); // Insert
        })
      );
      onReorderedStep({
        stepGroupType: stepGroups[stepGroupIndex].group,
        step: stepGroups[stepGroupIndex].steps[stepIndex],
        direction,
      });
    },
    [onReorderedStep, onChangeSteps, stepGroups]
  );

  const moveStepArbitrarily = useCallback(
    (stepCidToMove: string, targetStepCid: string) => {
      assert(!props.locked, 'Should not be able to drag step for locked procedure');

      onChangeSteps((sg) => {
        const result = _moveStepArbitrarily(sg, stepCidToMove, targetStepCid);
        if (result != null) {
          onReorderedStep({
            step: result.stepBeingMoved,
            stepGroupType: result.stepGroupToMoveFrom,
            direction: 'drag',
          });
          return result.newStepGroups;
        }
        return sg; // No change
      });
    },
    [onChangeSteps, onReorderedStep, props.locked]
  );

  /**
   Method to delete the step at the given index
   */
  const deleteStep = useCallback(
    (stepGroupIndex: number, stepIndex: number) => {
      onChangeSteps((sg) =>
        produce(sg, (draftStepGroups) => {
          const {steps} = draftStepGroups[stepGroupIndex];
          const [removedStep] = steps.splice(stepIndex, 1); // Remove
          if (removedStep.documentId) {
            DocumentActions.deleteDocument(removedStep.documentId);
          }
          const newStepCid = getNextStepCidAfterRemove(steps, draftStepGroups, stepIndex);
          onChangeSelection(newStepCid ?? null);
        })
      );
    },
    [onChangeSelection, onChangeSteps]
  );

  /**
   Method to add a new step or if stepIndex is provided, duplicate step from the original
   */
  const addStep = useCallback(
    async (stepGroupIndex: number, stepIndex: number, stepName?: string, fromId?: string): Promise<void> => {
      // When adding a new step, we need to trigger an immediate API call, which will create the
      // document to associate with this step

      if (powSaveImmediately) {
        // TODO: Until `powSaveImmediately` is implemented as a callback, this will cause double saves.
        //   The call to `powSaveImmediately` here will save, and then the call to `onChangeSteps` will
        //   trigger a save via the normal autosave mechanism (as it causes the procedure to become dirty).

        // TODO(BNCH-132164): The document associated with the new step will not be visible until the user
        //   does a hard refresh. It will just be a spinner, since the CIDs returned from the save call are
        //   not merged into the procedure (see ProcedureRootNodeAdapter.onSaveComplete)

        onChangeSteps((sg) => {
          const {updatedStepGroups, newStepCid} = addStepDirectlyToStepGroup(
            sg,
            stepGroupIndex,
            stepIndex,
            stepName,
            fromId
          );
          onChangeSelection(newStepCid);

          powSaveImmediately({...method, stepGroups: updatedStepGroups});
          return updatedStepGroups;
        });
      } else {
        await saveImmediately((latestFlowchart: ProcedureFlowchart) => {
          onChangeSteps((sg) => {
            const {updatedStepGroups, newStepCid} = addStepDirectlyToStepGroup(
              sg,
              stepGroupIndex,
              stepIndex,
              stepName,
              fromId
            );
            onChangeSelection(newStepCid);
            return updatedStepGroups;
          });
          return {
            blockingSaveObjectSource: BlockingSaveObjectSource.Step,
            blockingSaveOperation: BlockingSaveOperation.Create,
          };
        }, ADDING_STEP_TEXT);
      }
    },
    [method, onChangeSteps, onChangeSelection, saveImmediately, powSaveImmediately]
  );

  /**
   Method to duplicate step from the original
   */
  const copyStep = useCallback(
    async (stepGroupIndex: number, stepIndex: number): Promise<void> => {
      // When adding a new step, we need to trigger an immediate API call, which will create the
      // document to associate with this step
      if (powSaveImmediately) {
        // TODO: See comments above about double saves
        // TODO(BNCH-132164): See comments above about new documents not being visible until hard refresh

        onChangeSteps((sg) => {
          // TODO: the persistenceInfo is not used here, because powSaveImmediately does not support returning it.
          //   That means the map of {newStepCid: documentId} is not sent to the server
          const {newStep} = copyStepWithPersistenceInfo(sg, stepGroupIndex, stepIndex);
          const updatedStepGroups = getUpdatedStepGroupsWithCopiedStep(
            sg,
            stepGroupIndex,
            stepIndex,
            newStep
          );

          onChangeSelection(newStep.cid);
          powSaveImmediately({...method, stepGroups: updatedStepGroups});
          return updatedStepGroups;
        });
      } else {
        await saveImmediately((_latestFlowchart: ProcedureFlowchart) => {
          // Copy the step and prepare the persistence info to return to the saveImmediately call. This
          // does not update the step groups yet, because onChangeSteps will do that. It needs to happen
          // onChangeSteps callback is executed in order to avoid race conditions or overwriting any changes
          // to the step groups.
          const {newStep, persistenceInfo} = copyStepWithPersistenceInfo(
            stepGroups,
            stepGroupIndex,
            stepIndex
          );

          onChangeSteps((latestStepGroups) => {
            const updatedStepGroups = getUpdatedStepGroupsWithCopiedStep(
              latestStepGroups,
              stepGroupIndex,
              stepIndex,
              newStep
            );
            onChangeSelection(newStep.cid);
            return updatedStepGroups;
          });

          return persistenceInfo;
        }, 'Copying step...');
      }
    },
    [onChangeSteps, onChangeSelection, saveImmediately, powSaveImmediately, stepGroups, method]
  );

  /**
   Method to delete the step after providing a confirmation modal to the user
   */
  const deleteStepWithConfirmation = useCallback(
    async (stepGroupIndex: number, stepIndex: number) => {
      const step = stepGroups[stepGroupIndex].steps[stepIndex];
      const documentId = step.documentId;
      assert(documentId != null, 'Unexpectedly missing document ID');
      const document = DocumentStore.getDocument(documentId);
      assert(document != null, 'Unexpectedly missing document');

      if (DocumentUtil.isDocumentEmpty(document) || (await runConfirmDeleteModal({typeName: 'step'}))) {
        deleteStep(stepGroupIndex, stepIndex);
      }
    },
    [deleteStep, stepGroups]
  );

  /**
   Method to generate the overflow menu for the step list items
   */
  const makeOverflowMenu = useCallback(
    (stepGroupIndex: number, stepIndex: number, numberOfStepsInGroup: number): Array<OverflowMenuOption> => [
      {
        icon: 'copy',
        contents: (
          <DuplicateStepOptionWithStepLimit
            isStepLimitReached={isStepLimitReached}
            procedureType={procedureType}
          />
        ),
        onSelect: async () => {
          await copyStep(stepGroupIndex, stepIndex);
        },
        isDisabled: isStepLimitReached,
      },
      {
        icon: 'arrow-up',
        contents: <>Move up</>,
        onSelect: () => {
          moveStep(stepGroupIndex, stepIndex, -1);
        },
        isDisabled: stepIndex === 0,
      },
      {
        icon: 'arrow-down',
        contents: <>Move down</>,
        onSelect: () => {
          moveStep(stepGroupIndex, stepIndex, +1);
        },
        isDisabled: stepIndex === numberOfStepsInGroup - 1,
      },
      {
        icon: 'trash-can',
        contents: <>Remove</>,
        onSelect: () => {
          deleteStepWithConfirmation(stepGroupIndex, stepIndex);
        },
        isDisabled: false,
      },
    ],
    [copyStep, deleteStepWithConfirmation, isStepLimitReached, moveStep, procedureType]
  );

  /**
   Method to open the modal when the user clicks on the add step + button on the step group header
   */
  const onAddStepClicked = useCallback(
    async (e: ElementMouseEvent, stepGroupIndex: number, stepGroup: ProcedureStepGroup) => {
      e.stopPropagation();
      if (currentlyEditingStepCid != null) {
        storeDraftName(currentlyEditingStepCid);
      }
      setCurrentlyEditingStepCid(null);
      await runModal(AddStepModal, {
        onRequestSubmit: async ({stepPlacement, stepName, fromId}) => {
          await addStep(
            stepGroupIndex,
            stepPlacement === StepPlacement.BOTTOM ? stepGroup.steps.length : 0,
            stepName,
            fromId
          );
        },
        stepGroupName: PROCEDURE_STEP_GROUP_TYPE_TO_LABEL[stepGroup.group],
        existingStepNames: collectAllStepNames(stepGroups),
      });
    },
    [addStep, currentlyEditingStepCid, stepGroups, storeDraftName]
  );

  /**
   Method to handle the user clicking on a list item to change the selected step rendered in the editor
   */
  const onClickListItem = useCallback(
    (step: ProcedureStepConfig) => {
      setCurrentlyEditingStepCid(null);
      onChangeSelection(step.cid);
    },
    [onChangeSelection]
  );

  return (
    <div
      className={css(styles.wrapper)}
      style={{width: `${props.sidebarWidth}px`}}
      {...getTestProps({component: 'StepSelectorSidebar', element: 'root'})}
    >
      {stepGroups.map((stepGroup, stepGroupIndex) => (
        <ProcedureStepGroupWrapper
          key={stepGroupIndex}
          name={PROCEDURE_STEP_GROUP_TYPE_TO_LABEL[stepGroup.group]}
          infoPopoverText={getInfoIconCopyForStepGroup(procedureType, stepGroup.group)}
          showLockedIcon={props.locked}
          onAddStepClicked={async (e) => {
            await onAddStepClicked(e, stepGroupIndex, stepGroup);
          }}
        >
          {stepGroup.steps.map((step, stepIndex) =>
            currentlyEditingStepCid === step.cid ? (
              <TextInput
                key={step.cid}
                value={draftName}
                onChange={(e: InputChangeEvent) => {
                  setDraftName(e.target.value);
                }}
                onFocus={onFocus}
                onBlur={() => {
                  setCurrentlyEditingStepCid(null);
                  onBlur();
                }}
                onKeyDown={handleEnterAndEscape}
                className={css(styles.stepNameEditor)}
                autoFocus={true}
                placeholder={STEP_NAME_PLACEHOLDER}
                disabled={props.locked}
                maxLength={MAX_STEP_NAME_LENGTH}
              />
            ) : (
              <ProcedureStepsListItem
                key={step.cid}
                stepCid={step.cid}
                name={props.selectedStepCid === step.cid ? draftName : step.name || STEP_NAME_PLACEHOLDER}
                nameClassName={makePlaceholderNameClass(step.name)}
                isSelected={props.selectedStepCid === step.cid}
                onClick={() => {
                  onClickListItem(step);
                }}
                onDoubleClick={() => {
                  if (!props.locked) {
                    setCurrentlyEditingStepCid(step.cid);
                  }
                }}
                overflowMenuItems={
                  !props.locked
                    ? makeOverflowMenu(stepGroupIndex, stepIndex, stepGroup.steps.length)
                    : undefined
                }
                dragAndDropProperties={dragDropTools.makeDragPropsForItemByCid(step.cid)}
              />
            )
          )}
          <AddStepButton
            group={stepGroup.group}
            onClick={() => {
              // Ensure that any open text editor is closed before proceeding
              setCurrentlyEditingStepCid(null);
              setTimeout(async () => {
                await addStep(stepGroupIndex, stepGroup.steps.length);
              });
            }}
            isStepLimitReached={isStepLimitReached}
            locked={props.locked}
            dragAndDropProperties={dragDropTools.makeDragPropsForItemByCid(
              makeCidForAddStepButton(stepGroup.group)
            )}
          />
        </ProcedureStepGroupWrapper>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    borderRight: `1px solid ${GRAY_LIGHT}`,
    overflowY: 'scroll',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  addStepButtonWrapper: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    background: WHITE,
    position: 'relative',
  },
  stepNameEditor: {
    height: '40px',
    fontSize: 13,
    padding: '7px',
    background: BLUE_POWDER_LIGHT,
    border: 0,
    borderTop: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '0',
  },
  elementBeingHoveredOver: {
    '::before': {
      content: `"${NON_BREAKING_SPACE}"`,
      lineHeight: '1px',
      display: 'block',
      width: '100%',
      position: 'absolute',
      top: 0,
      borderTop: `3px solid ${Color.BLUE_450}`,
    },
  },
});
